import { Injectable, inject, signal, computed } from '@angular/core'
import {
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  Unsubscribe,
} from 'firebase/firestore'

import { Organization, OrganizationMember } from '../models/organization.model'
import { Role, DEFAULT_MEMBER_PERMISSIONS } from '../models/role.model'
import { UserData, PartialUserPermissions, resolveUserPermissions } from '../models/user.model'
import { DataStoreService } from './data-store.service'
import { FirebaseService } from './firebase.service'

/** Identity of this app in the shared product-access system (see `productAccessAllowed`). */
const PRODUCT_ID = 'chat'

/** Shape of `organizations/{orgId}/product-access/config`. */
interface ProductAccessConfig {
  enabledProducts: string[]
  roleProductAccess: Record<string, string[]>
}

type ChatKnowledgeStatus = 'disabled' | 'provisioning' | 'ready' | 'error'

/** Shape of `organizations/{orgId}/product-access/chat`. */
interface ChatKnowledgeConfig {
  enabled: boolean
  status: ChatKnowledgeStatus
  roleAccess?: string[]
  accountId?: string
  collectionId?: string
}

/**
 * Tracks the user's current organization, membership, and effective role, and
 * keeps them live via Firestore subscriptions.
 *
 * This is a chat-ui adaptation of admin-console's `OrgContextService`. The
 * doc shapes and field mappings into `DataStoreService` are faithful to
 * admin-console, but the org-MANAGEMENT surface (Microsoft/Entra role sync,
 * invite flows, role CRUD + seeding, member pagination, per-service
 * sessionStorage caching) is intentionally dropped — chat-ui only READS the
 * current org/member/role to know which org the user is in and what
 * permissions they hold. The Firestore reads admin-console delegated to
 * `OrganizationService` / `OrganizationMemberService` / `RoleService` (which
 * do not exist in chat-ui and are not introduced anywhere in the rewrite
 * plan) are inlined here.
 */
@Injectable({ providedIn: 'root' })
export class OrgContextService {
  private readonly firebase = inject(FirebaseService)
  private readonly dataStore = inject(DataStoreService)

  readonly currentOrg = computed(() => this.dataStore.currentOrganization())
  readonly currentMember = computed(() => this.dataStore.currentMember())
  readonly currentRole = computed(() => this.dataStore.currentRole())

  readonly userOrganizations = signal<Organization[]>([])
  readonly loading = signal(false)

  /**
   * Product IDs the current org has enabled, mirrored live from
   * `organizations/{orgId}/product-access/config.enabledProducts`. Drives the
   * sidebar's cross-product switcher (which apps to offer). Empty on a missing
   * config doc or read error — never blocks the UI.
   * See `productAccessAllowed` below for the actual `/product-disabled` gate,
   * which reads the same doc but resolves missing-doc/error differently.
   */
  readonly enabledProducts = signal<string[]>([])

  /**
   * Raw product-access config, used only by `productAccessAllowed`. Three
   * states, driven from the same `onSnapshot` as `enabledProducts`:
   *  - `productAccessResolved() === false` (initial, before the first
   *    snapshot callback fires) → treated as allowed (optimistic, not
   *    blocking).
   *  - resolved + `productAccessConfig() === null` → the doc was read and
   *    confirmed NOT to exist → gate evaluates to blocked (a legacy org with
   *    no product-access doc has no products enabled).
   *  - resolved + a config object → normal two-factor evaluation. On a
   *    snapshot READ ERROR (as opposed to a missing doc) this is set to a
   *    permissive sentinel (`enabledProducts: [PRODUCT_ID]`) so a transient
   *    Firestore error fails OPEN.
   */
  private readonly productAccessResolved = signal(false)
  private readonly productAccessConfig = signal<ProductAccessConfig | null>(null)
  private readonly chatKnowledgeConfig = signal<ChatKnowledgeConfig | null>(null)

  /**
   * Live-synced `organizations/{orgId}/billing/settings.lowBalanceThresholdCredits`,
   * normalized to a positive finite number or `null` (missing/invalid doc, no
   * threshold configured, or no org — see the subscription in
   * `startCurrentOrgSync`). Drives the low-balance toast (`lowBalanceActive`
   * below).
   */
  readonly lowBalanceThresholdSig = signal<number | null>(null)

  /**
   * Org-admin check for the low-balance-toast gate: owner OR a role with the
   * wildcard permission. Distinct from `hasPermission`, which
   * falls back to `isCurrentUserOwner()` only when there is no role at all —
   * this only cares about admin-equivalence.
   */
  readonly isCurrentOrgAdmin = computed(
    () => (this.currentRole()?.permissions?.includes('*') ?? false) || this.isCurrentUserOwner(),
  )

  /**
   * Show-condition for the low-balance toast: an org is loaded, a positive
   * threshold is configured, the current user is an org admin, and the
   * rounded-down balance is at or below that threshold. Session dismissal is
   * tracked by the banner because sessionStorage is not reactive.
   */
  readonly lowBalanceActive = computed(() => {
    const org = this.currentOrg()
    if (!org?.id) return false
    const threshold = this.lowBalanceThresholdSig()
    if (threshold === null) return false
    if (!this.isCurrentOrgAdmin()) return false
    return (org.creditBalance ?? 0) <= threshold
  })

  /**
   * Two-factor gate for this product (`chat`): the org must have `chat` in
   * `enabledProducts` AND, if the current role has a non-empty
   * `roleProductAccess` entry, that entry must also include `chat` (an
   * absent/empty role entry means no role-level restriction). "Not yet
   * resolved" fails open so the UI is never blocked before real data arrives;
   * a confirmed-missing config doc fails closed.
   * `layout.component.ts` watches this to redirect to `/product-disabled`.
   */
  readonly productAccessAllowed = computed<boolean>(() => {
    if (!this.productAccessResolved()) return true

    const cfg = this.productAccessConfig()
    if (!cfg) return false
    if (!cfg.enabledProducts.includes(PRODUCT_ID)) return false

    const roleId = this.currentRole()?.id ?? null
    if (roleId) {
      const roleProducts = cfg.roleProductAccess[roleId]
      if (Array.isArray(roleProducts) && roleProducts.length > 0 && !roleProducts.includes(PRODUCT_ID)) {
        return false
      }
    }
    return true
  })

  /**
   * Folder Knowledge is a Chat feature, so its gate extends the existing Chat
   * organization/role decision with the live Knowledge configuration and its
   * optional explicit role allowlist. Unlike the app-level Chat redirect,
   * Knowledge fails closed until both documents have resolved.
   */
  readonly folderKnowledgeAccessAllowed = computed<boolean>(() => {
    if (!this.productAccessResolved()) return false

    // flag: Folder Knowledge requires organization-level and role-level Chat access.
    if (!this.productAccessAllowed()) return false

    const config = this.chatKnowledgeConfig()
    if (
      !config?.enabled ||
      config.status !== 'ready' ||
      !config.collectionId?.trim()
    ) {
      return false
    }

    // flag: An absent Knowledge role allowlist permits every Chat-authorized role; a present list is explicit.
    if (config.roleAccess !== undefined) {
      const roleId = this.currentRole()?.id
      if (!roleId || !config.roleAccess.includes(roleId)) return false
    }
    return true
  })

  readonly folderKnowledgeCollectionId = computed<string | null>(() => {
    if (!this.folderKnowledgeAccessAllowed()) return null
    return this.chatKnowledgeConfig()?.collectionId?.trim() || null
  })

  private currentOrgUnsub: Unsubscribe | null = null
  private currentMemberUnsub: Unsubscribe | null = null
  private currentRoleUnsub: Unsubscribe | null = null
  private productAccessUnsub: Unsubscribe | null = null
  private chatKnowledgeUnsub: Unsubscribe | null = null
  private lowBalanceUnsub: Unsubscribe | null = null
  private subscribedOrgId: string | null = null
  private subscribedRoleId: string | null = null

  // The org the user last selected, shared across all Crewdle apps via the user
  // doc (`users/{uid}.currentOrganizationId`). Captured in loadUserOrganizations.
  private remoteCurrentOrgId: string | null = null

  private get storageKey(): string {
    const uid = this.firebase.auth?.currentUser?.uid
    return `chat-ui-current-org-${uid}`
  }

  private getSavedOrgId(): string | null {
    const saved = localStorage.getItem(this.storageKey)
    const legacy = localStorage.getItem('crewdle_orgId')
    if (!saved && legacy) localStorage.setItem(this.storageKey, legacy)
    return saved ?? legacy
  }

  async init(): Promise<void> {
    // Keep initialization safe if Firebase could not be constructed.
    if (!this.firebase.auth || !this.firebase.firestore) {
      return
    }

    await this.firebase.auth.authStateReady()
    const user = this.firebase.auth.currentUser
    if (!user) return

    await this.loadUserOrganizations()

    // Restore the active org. The user-doc `currentOrganizationId` is the
    // cross-app source of truth; fall back to this app's localStorage, then
    // the first org.
    const orgs = this.userOrganizations()
    const savedOrgId = this.getSavedOrgId()
    const target = [this.remoteCurrentOrgId, savedOrgId].find(
      (id): id is string => !!id && orgs.some((o) => o.id === id),
    )

    if (target) {
      await this.switchOrg(target)
    } else if (orgs.length > 0) {
      await this.switchOrg(orgs[0].id)
    }
  }

  async loadUserOrganizations(): Promise<void> {
    const auth = this.firebase.auth
    const db = this.firebase.firestore
    if (!auth || !db) return

    const user = auth.currentUser
    if (!user) return

    const userDoc = await getDoc(doc(db, 'users', user.uid))
    const userData = userDoc.data()
    const orgIds: string[] = userData?.['organizationIds'] || []
    // Cross-app shared selection (same field other Crewdle apps use).
    this.remoteCurrentOrgId = (userData?.['currentOrganizationId'] as string) || null

    // Publish the Firestore user doc into the store. This is the only writer;
    // do it before returning so ChatService/MessageService/StorageService (which
    // read `dataStore.currentUser()!.userId`) never observe a null user once
    // init()/switchOrg() have completed.
    this.dataStore.updateState({
      currentUser: {
        ...(userData as Partial<UserData>),
        userId: user.uid,
        email: userData?.['email'] ?? user.email ?? '',
        name: userData?.['name'] ?? user.displayName ?? '',
        organizationIds: orgIds,
        currentOrganizationId: this.remoteCurrentOrgId ?? undefined,
        // Prefer the Firebase-Storage-backed field admin-console writes
        // (`profilePictureUrl`), falling back to the legacy `profile_image_url`
        // field; OAuth `photoURL` is deliberately ignored so a
        // cross-app-uploaded avatar always wins.
        profile_image_url: (userData?.['profilePictureUrl'] as string | undefined)
          ?? (userData?.['profile_image_url'] as string | undefined)
          ?? undefined,
        // Resolve with defaults so downstream gates (system prompt, export,
        // temporary-chat enforcement, notes) can read `permissions.chat.*`/
        // `permissions.features.*` directly without each needing its own
        // `?? true` fallback.
        permissions: resolveUserPermissions(userData?.['permissions'] as PartialUserPermissions | undefined),
      } as UserData,
    })

    // Load every org the user belongs to in parallel — this is on the cold
    // bootstrap path, so N serial round trips here directly delay first paint.
    // Orgs the user can no longer access resolve to null and are dropped.
    const orgs = (
      await Promise.all(
        orgIds.map(async (orgId) => {
          try {
            const snap = await getDoc(doc(db, 'organizations', orgId))
            return snap.exists() ? this.mapOrganization(orgId, snap.data()) : null
          } catch (error) {
            // A stale membership pointer is expected and should not prevent us
            // trying the user's other organizations. Network/unavailable and
            // other bootstrap failures are different: propagate them so the
            // auth guard fails closed rather than entering the app with no org.
            const code = (error as { code?: string } | null)?.code
            if (code === 'permission-denied' || code === 'firestore/permission-denied') return null
            throw error
          }
        }),
      )
    ).filter((o): o is Organization => o !== null)

    this.userOrganizations.set(orgs)
  }

  async switchOrg(orgId: string): Promise<void> {
    if (!this.firebase.auth || !this.firebase.firestore) return

    this.loading.set(true)
    try {
      // switchOrg is always called with an org already present in
      // userOrganizations (init() loads it first), so reuse that loaded doc;
      // startCurrentOrgSync below opens a live subscription that reconciles any
      // drift within milliseconds.
      const userOrgs = this.userOrganizations()
      const organization = userOrgs.find((o) => o.id === orgId)
      if (!organization) {
        console.error('[OrgContext] switchOrg: user does not have access to organization', orgId)
        throw new Error('Unauthorized: user is not a member of this organization')
      }

      const uid = this.firebase.auth.currentUser?.uid
      const db = this.firebase.firestore

      let member: OrganizationMember | null = null
      let role: Role | null = null

      if (uid) {
        try {
          const memberSnap = await getDoc(doc(db, 'organizations', orgId, 'members', uid))
          if (memberSnap.exists()) {
            member = this.mapMember(memberSnap.data())
          }
        } catch {
          // Member doc may not exist yet.
        }
      }

      if (member) {
        const roleId = member.roleId || member.role || null
        try {
          role = await this.loadRole(orgId, roleId, member, organization)
        } catch {
          role = this.buildFallbackRoleFromMember(member, organization)
        }
      }

      // Fallback: if still no role but user is org owner, grant admin.
      if (!role && organization.ownerId === uid) {
        role = this.buildAdminRole()
      }

      this.dataStore.updateState({
        currentOrganization: organization,
        currentMember: member,
        currentRole: role,
      })

      localStorage.setItem(this.storageKey, orgId)
      // Keep the unscoped key for older Crewdle apps which still share it.
      localStorage.setItem('crewdle_orgId', orgId)
      // Persist the selection on the user doc so every Crewdle app picks up the
      // same active org. Only write on an actual change — init() calls
      // switchOrg on every load.
      if (uid && this.remoteCurrentOrgId !== orgId) {
        setDoc(
          doc(db, 'users', uid),
          { currentOrganizationId: orgId },
          { merge: true },
        ).catch(() => undefined)
      }
      this.remoteCurrentOrgId = orgId
      this.startCurrentOrgSync(orgId)
    } finally {
      this.loading.set(false)
    }
  }

  /**
   * Keep the current organization, the user's membership, and their role in
   * sync so remote mutations (e.g. agent-driven rename or a role change)
   * reflect live in the UI. Opens three subscriptions:
   *   1. `organizations/{orgId}`               -> currentOrganization
   *   2. `organizations/{orgId}/members/{uid}` -> currentMember
   *   3. `organizations/{orgId}/roles/{roleId}`-> currentRole (re-subscribed
   *      whenever the member's roleId changes).
   */
  startCurrentOrgSync(orgId: string): void {
    if (!this.firebase.firestore) return
    if (this.subscribedOrgId === orgId) return

    const db = this.firebase.firestore

    this.currentOrgUnsub?.()
    this.currentMemberUnsub?.()
    this.currentRoleUnsub?.()
    this.productAccessUnsub?.()
    this.chatKnowledgeUnsub?.()
    this.lowBalanceUnsub?.()
    this.currentRoleUnsub = null
    this.productAccessUnsub = null
    this.chatKnowledgeUnsub = null
    this.lowBalanceUnsub = null
    this.subscribedRoleId = null
    this.subscribedOrgId = orgId
    this.productAccessResolved.set(false)
    this.productAccessConfig.set(null)
    this.chatKnowledgeConfig.set(null)
    this.lowBalanceThresholdSig.set(null)

    const orgRef = doc(db, 'organizations', orgId)
    this.currentOrgUnsub = onSnapshot(
      orgRef,
      (snap) => {
        if (!snap.exists()) return
        const current = this.dataStore.getProperty('currentOrganization')
        if (!current || current.id !== orgId) return
        const data = snap.data()
        const updated: Organization = {
          ...current,
          name: data['name'] ?? current.name,
          tids: data['tids'] ?? current.tids,
          usageReportSegments: data['usageReportSegments'] ?? current.usageReportSegments,
          creditBalance: data['creditBalance'] ?? current.creditBalance,
          // flag: Folder Knowledge always uses the organization's live canonical Chat account.
          accounts: data['accounts'] ?? current.accounts,
          milestones: data['milestones'] ?? current.milestones,
          onboardingProgram: data['onboardingProgram'] ?? current.onboardingProgram,
        }
        this.dataStore.updateState({ currentOrganization: updated })
      },
      () => {
        // Transient read error (e.g. stale auth token after the tab was
        // backgrounded past its lifetime): keep the last known org rather
        // than letting an unhandled listener error reach the global error
        // handler and crash the app.
      },
    )

    // Mirror the org's enabled products for the sidebar product switcher. This
    // is independent of the user's uid/role — it only reflects what the org has
    // turned on. Missing doc or read error => empty (switcher shows Admin only).
    // The SAME subscription also drives `productAccessAllowed` (the
    // `/product-disabled` gate) via `productAccessConfig`/`productAccessResolved`
    // above — see their doc comments for why missing-doc and read-error are
    // handled differently there.
    const productAccessRef = doc(db, 'organizations', orgId, 'product-access', 'config')
    this.productAccessUnsub = onSnapshot(
      productAccessRef,
      (snap) => {
        if (this.subscribedOrgId !== orgId) return
        const data = snap.data()
        this.enabledProducts.set((data?.['enabledProducts'] as string[]) ?? [])
        this.productAccessConfig.set(
          snap.exists()
            ? {
                enabledProducts: (data?.['enabledProducts'] as string[]) ?? [],
                roleProductAccess: (data?.['roleProductAccess'] as Record<string, string[]>) ?? {},
              }
            : null,
        )
        this.productAccessResolved.set(true)
      },
      () => {
        // Read error (not a missing doc): fail OPEN so a transient Firestore
        // error never locks a user out mid-session.
        this.enabledProducts.set([])
        this.productAccessConfig.set({ enabledProducts: [PRODUCT_ID], roleProductAccess: {} })
        this.productAccessResolved.set(true)
      },
    )

    // flag: Chat UI consumes the live organization Knowledge toggle, provisioning state, role allowlist, and collection ID.
    const chatKnowledgeRef = doc(db, 'organizations', orgId, 'product-access', 'chat')
    this.chatKnowledgeUnsub = onSnapshot(
      chatKnowledgeRef,
      (snap) => {
        if (this.subscribedOrgId !== orgId) return
        if (!snap.exists()) {
          this.chatKnowledgeConfig.set(null)
          return
        }

        const data = snap.data()
        const rawStatus = data['status']
        const status: ChatKnowledgeStatus =
          rawStatus === 'disabled' ||
          rawStatus === 'provisioning' ||
          rawStatus === 'ready' ||
          rawStatus === 'error'
            ? rawStatus
            : 'disabled'
        const rawRoleAccess = data['roleAccess']
        this.chatKnowledgeConfig.set({
          enabled: data['enabled'] === true,
          status,
          roleAccess: Array.isArray(rawRoleAccess)
            ? rawRoleAccess.filter((roleId): roleId is string => typeof roleId === 'string')
            : undefined,
          accountId: typeof data['accountId'] === 'string' ? data['accountId'] : undefined,
          collectionId:
            typeof data['collectionId'] === 'string' ? data['collectionId'] : undefined,
        })
      },
      () => {
        // Knowledge is optional; a read failure must never expose it without a verified ready config.
        this.chatKnowledgeConfig.set(null)
      },
    )

    // Low-balance-toast threshold. Read error or missing doc/field both
    // normalize to `null` (no threshold -> toast never shows), a fail-safe
    // that never blocks the UI.
    const billingSettingsRef = doc(db, 'organizations', orgId, 'billing', 'settings')
    this.lowBalanceUnsub = onSnapshot(
      billingSettingsRef,
      (snap) => {
        if (this.subscribedOrgId !== orgId) return
        const v = snap.data()?.['lowBalanceThresholdCredits']
        this.lowBalanceThresholdSig.set(typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null)
      },
      () => {
        this.lowBalanceThresholdSig.set(null)
      },
    )

    const uid = this.firebase.auth?.currentUser?.uid
    if (!uid) return
    const memberRef = doc(db, 'organizations', orgId, 'members', uid)
    this.currentMemberUnsub = onSnapshot(
      memberRef,
      (snap) => {
        if (this.subscribedOrgId !== orgId) return
        if (!snap.exists()) {
          this.dataStore.updateState({ currentMember: null })
          this.resubscribeRole(orgId, null)
          return
        }
        const updatedMember = this.mapMember(snap.data())
        this.dataStore.updateState({ currentMember: updatedMember })
        const roleId = updatedMember.roleId || updatedMember.role || null
        this.resubscribeRole(orgId, roleId)
      },
      () => {
        // Transient read error: keep the last known member/role rather than
        // letting an unhandled listener error crash the app.
      },
    )
  }

  private resubscribeRole(orgId: string, roleId: string | null): void {
    if (this.subscribedRoleId === roleId) return
    this.currentRoleUnsub?.()
    this.currentRoleUnsub = null
    this.subscribedRoleId = roleId

    if (!roleId) {
      this.dataStore.updateState({ currentRole: this.fallbackRoleFromState() })
      return
    }

    if (roleId === 'admin') {
      this.dataStore.updateState({ currentRole: this.buildAdminRole() })
      return
    }

    const db = this.firebase.firestore
    if (!db) return
    const roleRef = doc(db, 'organizations', orgId, 'roles', roleId)
    this.currentRoleUnsub = onSnapshot(
      roleRef,
      (snap) => {
        if (this.subscribedOrgId !== orgId || this.subscribedRoleId !== roleId) return
        if (!snap.exists()) {
          this.dataStore.updateState({ currentRole: this.fallbackRoleFromState() })
          return
        }
        this.dataStore.updateState({ currentRole: this.mapRole(snap.id, snap.data()) })
      },
      () => {
        // Transient read error: keep the last known role rather than letting
        // an unhandled listener error crash the app.
      },
    )
  }

  stopCurrentOrgSync(): void {
    this.currentOrgUnsub?.()
    this.currentMemberUnsub?.()
    this.currentRoleUnsub?.()
    this.productAccessUnsub?.()
    this.chatKnowledgeUnsub?.()
    this.lowBalanceUnsub?.()
    this.currentOrgUnsub = null
    this.currentMemberUnsub = null
    this.currentRoleUnsub = null
    this.productAccessUnsub = null
    this.chatKnowledgeUnsub = null
    this.lowBalanceUnsub = null
    this.subscribedOrgId = null
    this.subscribedRoleId = null
    this.enabledProducts.set([])
    this.productAccessResolved.set(false)
    this.productAccessConfig.set(null)
    this.chatKnowledgeConfig.set(null)
    this.lowBalanceThresholdSig.set(null)
  }

  hasPermission(permission: string) {
    return computed(() => {
      const role = this.currentRole()
      if (!role) {
        return this.isCurrentUserOwner()
      }
      return role.permissions.includes(permission) || role.permissions.includes('*')
    })
  }

  async checkPermission(permission: string): Promise<boolean> {
    const role = this.currentRole()
    if (!role) {
      return this.isCurrentUserOwner()
    }
    return role.permissions.includes(permission) || role.permissions.includes('*')
  }

  private isCurrentUserOwner(): boolean {
    const org = this.currentOrg()
    const uid = this.firebase.auth?.currentUser?.uid
    return !!org && !!uid && org.ownerId === uid
  }

  /**
   * Resolve the role for a member: 'admin' short-circuits to a synthetic admin
   * role, an existing role doc is mapped, and anything else falls back to a
   * permission set derived from the member.
   */
  private async loadRole(
    orgId: string,
    roleId: string | null,
    member: OrganizationMember,
    organization: Organization,
  ): Promise<Role> {
    if (!roleId) return this.buildFallbackRoleFromMember(member, organization)
    if (roleId === 'admin') return this.buildAdminRole()

    const db = this.firebase.firestore
    if (!db) return this.buildFallbackRoleFromMember(member, organization)

    const roleSnap = await getDoc(doc(db, 'organizations', orgId, 'roles', roleId))
    if (!roleSnap.exists()) {
      return this.buildFallbackRoleFromMember(member, organization)
    }
    return this.mapRole(roleSnap.id, roleSnap.data())
  }

  private fallbackRoleFromState(): Role | null {
    const member = this.dataStore.getProperty('currentMember')
    const organization = this.dataStore.getProperty('currentOrganization')
    return member && organization
      ? this.buildFallbackRoleFromMember(member, organization)
      : null
  }

  private mapOrganization(id: string, data: Record<string, any>): Organization {
    return {
      ...(data as Organization),
      id,
    }
  }

  private mapMember(data: Record<string, any>): OrganizationMember {
    return {
      userId: data['userId'],
      role: data['role'],
      roleId: data['roleId'],
      permissions: data['permissions'],
      joinedAt: data['joinedAt'],
    }
  }

  private mapRole(id: string, data: Record<string, any>): Role {
    return {
      id,
      name: data['name'],
      description: data['description'] || '',
      permissions: data['permissions'] || [],
      isSystem: data['isSystem'] ?? true,
      createdAt: data['createdAt'],
      updatedAt: data['updatedAt'],
      createdBy: data['createdBy'] || '',
    }
  }

  private buildFallbackRoleFromMember(member: OrganizationMember, organization: Organization): Role {
    const roleId = member.roleId || member.role
    const isOwner = organization.ownerId === this.firebase.auth?.currentUser?.uid
    const isAdmin = isOwner || roleId === 'admin' || member.role === 'admin'
    return {
      id: roleId || 'member',
      name: (roleId || 'member').charAt(0).toUpperCase() + (roleId || 'member').slice(1),
      description: '',
      permissions: isAdmin ? ['*'] : DEFAULT_MEMBER_PERMISSIONS,
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    }
  }

  private buildAdminRole(): Role {
    return {
      id: 'admin',
      name: 'Admin',
      description: '',
      permissions: ['*'],
      isSystem: true,
      createdAt: null,
      updatedAt: null,
      createdBy: '',
    }
  }
}
